import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import DashboardLayout from "@/components/DashboardLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, Save, Euro, Tag } from "lucide-react";

function OverrideEditor({ overrides, onChange, clients }) {
  const update = (i, field, val) => {
    const next = overrides.map((o, idx) => (idx === i ? { ...o, [field]: val } : o));
    onChange(next);
  };
  return (
    <div className="space-y-2">
      {overrides.map((ov, i) => (
        <div key={i} className="flex items-center gap-2">
          <Select value={ov.client || undefined} onValueChange={(v) => update(i, "client", v)}>
            <SelectTrigger data-testid={`override-client-${i}`} className="h-8 text-xs flex-1">
              <SelectValue placeholder="Seleziona cliente..." />
            </SelectTrigger>
            <SelectContent>
              {clients.length === 0 ? (
                <div className="px-2 py-1.5 text-xs text-slate-400">Nessun cliente</div>
              ) : (
                clients.map((c) => (
                  <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <div className="relative w-28 shrink-0">
            <Input
              data-testid={`override-price-${i}`}
              type="number"
              value={ov.price}
              onChange={(e) => update(i, "price", parseFloat(e.target.value) || 0)}
              className="h-8 text-xs pr-6"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
          </div>
          <button data-testid={`override-remove-${i}`} onClick={() => onChange(overrides.filter((_, idx) => idx !== i))} className="text-rose-500 hover:text-rose-600 shrink-0">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button data-testid="override-add" onClick={() => onChange([...overrides, { client: "", price: 0 }])} className="text-xs text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1">
        <Plus className="h-3.5 w-3.5" /> Prezzo per cliente
      </button>
    </div>
  );
}

function PriceRow({ item, onSaved, onDeleted, clients, types }) {
  const [draft, setDraft] = useState(item);
  const [saving, setSaving] = useState(false);
  const isNew = !item.id;
  const dirty = JSON.stringify(draft) !== JSON.stringify(item);
  const typeOptions = draft.name && !types.includes(draft.name) ? [draft.name, ...types] : types;

  const save = async () => {
    if (!draft.name.trim()) { toast.error("Inserisci il nome della voce"); return; }
    setSaving(true);
    try {
      const payload = { name: draft.name, price: parseFloat(draft.price) || 0, per_element: draft.per_element, client_overrides: draft.client_overrides };
      const { data } = isNew ? await api.post("/price-list", payload) : await api.patch(`/price-list/${item.id}`, payload);
      toast.success("Salvato");
      onSaved(data, isNew);
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (isNew) { onDeleted(); return; }
    try {
      await api.delete(`/price-list/${item.id}`);
      toast.success("Voce eliminata");
      onDeleted();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-slate-200 p-4 bg-white" data-testid="price-row">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
        <div className="md:col-span-5">
          <label className="text-xs font-medium text-slate-500">Voce (lavorazione)</label>
          <Select value={draft.name || undefined} onValueChange={(v) => setDraft({ ...draft, name: v })}>
            <SelectTrigger data-testid="price-name" className="mt-1">
              <SelectValue placeholder="Seleziona lavorazione..." />
            </SelectTrigger>
            <SelectContent>
              {typeOptions.length === 0 ? (
                <div className="px-2 py-1.5 text-xs text-slate-400">Nessuna lavorazione</div>
              ) : (
                typeOptions.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3">
          <label className="text-xs font-medium text-slate-500">Prezzo base</label>
          <div className="relative mt-1">
            <Input data-testid="price-value" type="number" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} className="pr-7" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">€</span>
          </div>
        </div>
        <div className="md:col-span-4">
          <label className="text-xs font-medium text-slate-500">Calcolo</label>
          <div className="mt-2 flex items-center gap-2">
            <Switch data-testid="price-per-element" checked={draft.per_element} onCheckedChange={(v) => setDraft({ ...draft, per_element: v })} />
            <span className="text-sm text-slate-600">{draft.per_element ? "Per elemento (× quantità)" : "Forfait (una tantum)"}</span>
          </div>
        </div>
      </div>
      <div className="mt-3">
        <label className="text-xs font-medium text-slate-500">Prezzi dedicati per cliente (opzionale)</label>
        <div className="mt-2">
          <OverrideEditor overrides={draft.client_overrides} onChange={(ov) => setDraft({ ...draft, client_overrides: ov })} clients={clients} />
        </div>
      </div>
      <div className="mt-4 flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" data-testid="price-delete" onClick={remove} className="text-rose-500 hover:text-rose-600">
          <Trash2 className="h-4 w-4 mr-1" /> Elimina
        </Button>
        <Button size="sm" data-testid="price-save" onClick={save} disabled={saving || (!isNew && !dirty)} className="bg-teal-600 hover:bg-teal-700">
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Salva
        </Button>
      </div>
    </motion.div>
  );
}

export function PriceListPanel() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRows, setNewRows] = useState([]);

  const load = async () => {
    try {
      const [pl, cl, opt] = await Promise.all([api.get("/price-list"), api.get("/admin/clients"), api.get("/order-options")]);
      setItems(pl.data);
      const companies = [...new Set((cl.data || []).map((c) => c.company).filter(Boolean))].sort((a, b) => a.localeCompare(b));
      setClients(companies);
      const o = opt.data || {};
      const workTypes = [...new Set([...(o.materials || []), ...(o.design_types || [])].filter(Boolean))].sort((a, b) => a.localeCompare(b));
      setTypes(workTypes);
    } catch (e) {
      toast.error("Errore nel caricamento del listino");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const addRow = () => setNewRows((r) => [...r, { name: "", price: 0, per_element: true, client_overrides: [] }]);

  return (
    <>
      <div className="max-w-4xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Euro className="h-4 w-4 text-teal-600" />
            I prezzi qui inseriti popolano le formule del report Excel (Importo = Q.tà × Prezzo).
          </div>
          <Button data-testid="add-price-button" onClick={addRow} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="h-4 w-4 mr-1.5" /> Nuova voce
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
        ) : (
          <div className="space-y-3" data-testid="price-list">
            {newRows.map((row, i) => (
              <PriceRow
                key={`new-${i}`}
                item={row}
                clients={clients}
                types={types}
                onSaved={(data) => { setNewRows((r) => r.filter((_, idx) => idx !== i)); setItems((it) => [...it, data]); }}
                onDeleted={() => setNewRows((r) => r.filter((_, idx) => idx !== i))}
              />
            ))}
            {items.length === 0 && newRows.length === 0 ? (
              <Card className="p-12 text-center border-dashed">
                <Tag className="h-10 w-10 mx-auto text-slate-300" />
                <p className="mt-4 font-medium text-slate-700">Nessuna voce nel listino</p>
                <p className="text-sm text-slate-500">Aggiungi la prima voce con "Nuova voce".</p>
              </Card>
            ) : (
              items.map((it) => (
                <PriceRow
                  key={it.id}
                  item={it}
                  clients={clients}
                  types={types}
                  onSaved={(data) => setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)))}
                  onDeleted={() => setItems((arr) => arr.filter((x) => x.id !== it.id))}
                />
              ))
            )}
          </div>
        )}
      </div>
    </>
  );
}
